/**
 * Design: Meeting Row + Match Line (§3.2, screenshots/07–09).
 * Each row selects ONLY its own matches, so a live `match.add` re-renders one row.
 * New rows report "seen" after being ≥60% visible for 1 s; the "• New" dot then fades (motion 4).
 * Hovering or focusing a match line with a moment peeks at it (spec 11 card 2, Peek.tsx); a live
 * meeting's row gets the LIVE chip, the current speaker and audio bars (spec 11 card 1).
 */
import { memo, useEffect, useRef, useState } from "react";
import { CalendarPlus, Check, LoaderCircle } from "lucide-react";
import { PeekCard, usePeek } from "./Peek";
import { useShallow } from "zustand/react/shallow";
import { followingStore, useFollowing } from "../../stores/followingStore";
import { Face, FaceStack } from "../shared/Face";
import { TypeBadge } from "./TypeBadge";
import { ListRow, RowCell, RowLead, RowMain, RowTitle } from "../shared/List";
import { recipes } from "../../motion";
import { clock } from "../../lib/format";
import { downloadMeetingIcs } from "../../lib/ics";
import type { LiveNow, Match, MeetingSummary, Person } from "../../types";

const LINES_SHOWN = 3;
const NONE: string[] = [];
const day = (iso: string) => new Date(iso).toLocaleDateString("en-US", { timeZone: "America/Chicago", weekday: "short", month: "short", day: "numeric" });
const time = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" });

export const MatchLine = memo(function MatchLine({ m, people, href, meeting, peekKey, speaker }: {
  m: Match; people: Person[]; href: string; meeting?: MeetingSummary; peekKey?: string; speaker?: Person | null;
}) {
  const lead = m.type === "person" && people.length > 0;
  const canPeek = !!meeting && !!peekKey && m.at != null;
  const { open, lineProps, cardProps } = usePeek(peekKey ?? "", canPeek);
  return (
    <div className="match-wrap" data-peek={open || undefined}>
      <a className="match" data-type={m.type} href={href} {...lineProps}>
        {lead ? <FaceStack people={people} size={24} /> : <TypeBadge type={m.type} />}
        <span className="match__text">
          <span className="match__label">{m.label}</span>
          <span className="match__detail">{m.detail}</span>
        </span>
        {!lead && people.length > 0 && <FaceStack people={people} size={24} />}
      </a>
      {open && canPeek && <PeekCard m={m as Match & { at: number }} meeting={meeting!} speaker={speaker ?? people[0] ?? null} href={href} {...cardProps} />}
    </div>
  );
});

/** The live line (spec 11 card 1): "{label} speaking now", the current speaker's face and audio bars. */
function LiveLine({ live, people, href }: { live: LiveNow; people: Record<string, Person>; href: string }) {
  const speaker = live.speakerId ? people[live.speakerId] : null;
  return (
    <a className="match match--live" data-type={live.type} href={href}>
      {live.type === "person" && speaker ? <Face name={speaker.name} photoUrl={speaker.photoUrl} size={24} /> : <TypeBadge type={live.type} />}
      <span className="match__text">
        <span className="match__label">{live.label}</span>
        <span className="match__detail">{live.detail}</span>
      </span>
      {live.type !== "person" && speaker && <Face name={speaker.name} photoUrl={speaker.photoUrl} size={20} />}
      <i className="audio-bars" aria-hidden><i /><i /><i /><i /><i /></i>
    </a>
  );
}

function useSeenObserver(ref: React.RefObject<HTMLElement | null>, ids: string[], onSeen: (ids: string[]) => void) {
  useEffect(() => {
    const el = ref.current?.closest(".meeting-row") ?? ref.current;   // the ref sits in the row; watch the whole row
    if (!el || ids.length === 0) return;
    let t: ReturnType<typeof setTimeout> | undefined;
    const io = new IntersectionObserver(([e]) => {
      clearTimeout(t);
      if (e.intersectionRatio >= recipes.newToSeen.visibleThreshold) t = setTimeout(() => onSeen(ids), recipes.newToSeen.dwellMs);
    }, { threshold: [0, recipes.newToSeen.visibleThreshold] });
    io.observe(el);
    return () => { clearTimeout(t); io.disconnect(); };
  }, [ref, ids.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
}

export const MeetingRow = memo(function MeetingRow({ meeting, upcoming, filter, onSeen }: {
  meeting: MeetingSummary;
  upcoming: boolean;
  filter: (m: Match) => boolean;     // the type filter and search box
  onSeen: (ids: string[]) => void;   // sends {type:"seen"} on the org channel
}) {
  // Select stable references only (a fresh tuple per call would never compare equal and loop).
  const ids = useFollowing((s) => s.matchIdsByMeeting[meeting.id] ?? NONE);
  const matches = useFollowing(useShallow((s) => ids.map((id) => s.matchesById[id])));
  const seen = useFollowing(useShallow((s) => ids.map((id) => !!s.seenIds[id])));
  const people = useFollowing((s) => s.people);
  const live = useFollowing((s) => (s.live?.meetingId === meeting.id ? s.live : null));
  const rows = ids.map((id, i) => [id, matches[i], seen[i]] as const);
  const [all, setAll] = useState(false);
  const shown = rows.filter(([, m]) => filter(m));
  const newIds = rows.filter(([, m, seen]) => m.isNew && !seen).map(([id]) => id);
  const flagged = rows.some(([, m]) => m.isNew); // keeps the dot mounted so it can fade once seen
  const ref = useRef<HTMLDivElement>(null);
  useSeenObserver(ref, newIds, (ids) => { followingStore.getState().markSeen(ids); onSeen(ids); });
  const base = `/dashboard/meetings/${encodeURIComponent(meeting.id)}`;
  const lines = (all ? shown : shown.slice(0, LINES_SHOWN)).filter(([, m]) => !live || m.followId !== live.followId || m.label !== live.label);
  const liveHref = live ? `${base}?t=${clock(live.at)}` : base;

  return (
    <ListRow className="meeting-row" id={`meeting-${meeting.id}`} data-upcoming={(upcoming && !live) || undefined} data-live={live ? "" : undefined}>
      <RowLead size={84} className="meeting-row__lead">
        <div ref={ref} className="meeting-row__when">
          {live ? (
            <>
              <span className="live-chip"><i />LIVE</span>
              <span className="time">Today</span>
            </>
          ) : (
            <>
              <span className="date">{day(meeting.startsAt)}</span>
              <span className="time">{time(meeting.startsAt)}</span>
            </>
          )}
          {flagged && <span className="new-dot" data-seen={newIds.length === 0 || undefined} aria-hidden={newIds.length === 0 || undefined}>New</span>}
        </div>
      </RowLead>
      <RowMain min={100} className="meeting-row__main">
        <RowTitle lines={2} className="meeting-row__title">
          <a className="meeting-row__name" href={base}>
            <i className="body-mark" data-kind={meeting.bodyKind} />
            {meeting.body}
          </a>
        </RowTitle>
        {(lines.length > 0 || live) && (
          <div className="meeting-row__matches">
            {live && <LiveLine live={live} people={people} href={liveHref} />}
            {lines.map(([id, m]) => (
              <MatchLine key={id} m={m} href={m.at != null ? `${base}?t=${clock(m.at)}` : base} meeting={meeting} peekKey={id}
                speaker={people[m.speakerId ?? m.peopleIds?.[0] ?? ""] ?? null}
                people={(m.peopleIds ?? []).map((p) => people[p]).filter(Boolean)} />
            ))}
            {!all && shown.length > LINES_SHOWN && (
              <button className="match-more" onClick={() => setAll(true)}>+{shown.length - LINES_SHOWN} more</button>
            )}
          </div>
        )}
      </RowMain>
      <RowCell width={100} align="end" className="meeting-row__status">
        {live ? null : upcoming ? (
          <button className="btn btn--calendar" onClick={() => downloadMeetingIcs(meeting)}><CalendarPlus size={13} />Calendar</button>
        ) : meeting.transcriptStatus === "ready" ? (
          <span className="status status--ready"><Check size={13} />Transcript</span>
        ) : meeting.transcriptStatus === "processing" ? (
          <span className="status status--processing"><LoaderCircle size={13} />Transcribing…</span>
        ) : null}
      </RowCell>
    </ListRow>
  );
});
